import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Product } from '../types/product';

interface CompareContextValue {
  compareItems: Product[];
  isInCompare: (id: string | number) => boolean;
  addToCompare: (product: Product) => void;
  removeFromCompare: (id: string | number) => void;
  clearCompare: () => void;
  compareModalOpen: boolean;
  openCompareModal: () => void;
  closeCompareModal: () => void;
  // Compare mode: active when user clicked "+" to add another product
  compareModeActive: boolean;
  activateCompareMode: () => void;
  deactivateCompareMode: () => void;
}

const CompareContext = createContext<CompareContextValue | null>(null);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareItems, setCompareItems] = useState<Product[]>([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [compareModeActive, setCompareModeActive] = useState(false);

  const isInCompare = useCallback(
    (id: string | number) => compareItems.some((p) => p.id === id),
    [compareItems]
  );

  const addToCompare = useCallback((product: Product) => {
    setCompareItems((prev) => {
      if (prev.some((p) => p.id === product.id)) return prev;
      if (prev.length >= 3) return prev;
      return [...prev, product];
    });
    // Auto-deactivate compare mode after adding
    setCompareModeActive(false);
  }, []);

  const removeFromCompare = useCallback((id: string | number) => {
    setCompareItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareItems([]);
    setCompareModalOpen(false);
    setCompareModeActive(false);
  }, []);

  const openCompareModal = useCallback(() => setCompareModalOpen(true), []);
  const closeCompareModal = useCallback(() => setCompareModalOpen(false), []);
  const activateCompareMode = useCallback(() => setCompareModeActive(true), []);
  const deactivateCompareMode = useCallback(() => setCompareModeActive(false), []);

  return (
    <CompareContext.Provider
      value={{
        compareItems,
        isInCompare,
        addToCompare,
        removeFromCompare,
        clearCompare,
        compareModalOpen,
        openCompareModal,
        closeCompareModal,
        compareModeActive,
        activateCompareMode,
        deactivateCompareMode,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare(): CompareContextValue {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used inside CompareProvider');
  return ctx;
}
